import {
  AnalyticsChartElementWidth,
  AnalyticsChartSectorCalculateStatus,
  AnalyticsLinearChartCurveCalculateStatus,
  AnalyticsOrderingType,
  AnalyticsReportElementScaleV2,
  AnalyticsV2ChartType,
  AnalyticsV2ElementType,
  AnalyticsV2ElementWidth,
  BarChartDisplayType,
  ChartSettingsBaseDescriptor,
  LinearChartFunction,
  LinearChartStepType,
  ParameterForChartType,
  PieChartBundleDisplayType,
} from "@/models/etc/types";
import { CommonEntity, CommonEntityRecord } from "../../common";
import { ColumnSettingsSend } from "../columns/columns";
import { AnalyticsChartElementFormula, AnalyticsFormulaToken } from "../parameters/parameters";

export enum AnalyticsElementKind {
    Selection = 0,
    Projection = 1,
    List = 2
}
export interface AnalyticsElementIdentity {
    ElementId: number;
    Kind: AnalyticsElementKind;
    Source?: AnalyticsV2ElementType;
    Destination: AnalyticsV2ElementType;
}

export interface AnalyticsChartIdentity {
    ChartId: number;
    ChartType: AnalyticsV2ChartType;
    Destination: AnalyticsV2ElementType;
    ElementScale?: AnalyticsReportElementScaleV2;
}

export interface AnalyticsFolderIdentity {
    FolderId: number;
    Segment: AnalyticsV2ElementType;
}

/** Запрос создания списка аналитики (Lists/Create). */
export interface AnalyticsListCreateRequest {
  ElementIdentity: AnalyticsElementIdentity;
  SetFavorite: boolean;
}

/** Запрос перемещения элементов аналитики в корень сегмента (move-to-root-list). */
export interface AnalyticsElementsToRootListMoveRequest {
    TableElements: AnalyticsElementIdentity[];
    Charts: AnalyticsChartIdentity[];
    Folders: AnalyticsFolderIdentity[];
}

/** Запрос перемещения элементов аналитики в папку (move-to-folder). */
export interface AnalyticsElementsToFolderMoveRequest {
    TableElements: AnalyticsElementIdentity[];
    Charts: AnalyticsChartIdentity[];
    Folders: AnalyticsFolderIdentity[];
    TargetFolder: AnalyticsFolderIdentity;
}

/** Запрос перемещения элементов аналитики к новому родительскому табличному элементу (move-to-new-parent). */
export interface AnalyticsElementsToNewParentMoveRequest {
    TableElements: AnalyticsElementIdentity[];
    Charts: AnalyticsChartIdentity[];
    Folders: AnalyticsFolderIdentity[];
    TargetParentElement: AnalyticsElementIdentity;
}

// --- Details / actions DTOs (v13.4.11.0+) ---

export enum AnalyticsActionType {
  Read = 0,
  Write = 1,
}

export interface AnalyticsActionRecordDto {
  Creator: CommonEntity;
  CreateTime: Date | string;
  Type: AnalyticsActionType;
}

export interface AnalyticsPathSectionDto {
  TableElementIdentity?: AnalyticsElementIdentity | null;
  FolderIdentity?: AnalyticsFolderIdentity | null;
  ElementName: string;
}

export interface AnalyticsPathDto {
  Sections: AnalyticsPathSectionDto[];
}

export interface AnalyticsElementDetailsDto {
  CreateTime: Date | string;
  Creator: CommonEntity;
  Path: AnalyticsPathDto;
  WriteActions: AnalyticsActionRecordDto[];
  ReadActions: AnalyticsActionRecordDto[];
  WriteActionsTotalCount?: number;
  ReadActionsTotalCount?: number;
}

export interface AnalyticsFolderDetailsRequest {
  FolderIdentity: AnalyticsFolderIdentity;
  WriteActionsCount: number;
  ReadActionsCount: number;
}

export interface AnalyticsChartDetailsRequest {
  ChartIdentity: AnalyticsChartIdentity;
  WriteActionsCount: number;
  ReadActionsCount: number;
}

export interface AnalyticsTableElementDetailsRequest {
  TableElementIdentity: AnalyticsElementIdentity;
  WriteActionsCount: number;
  ReadActionsCount: number;
}

export interface AnalyticsActionRecordsLogFetchRequest {
  ElementIdentity?: AnalyticsElementIdentity | null;
  ChartIdentity?: AnalyticsChartIdentity | null;
  FolderIdentity?: AnalyticsFolderIdentity | null;
  Count: number;
  Offset: number;
  ActionType: AnalyticsActionType;
}

export interface ChartElementSettingsBase extends CommonEntityRecord {
    AnalyzedElementIdentity?: AnalyticsElementIdentity | null;
    Descriptor: ChartSettingsBaseDescriptor;
}

export interface ChartSettingsBase {
    Descriptor: ChartSettingsBaseDescriptor;
    ChartWidth: AnalyticsV2ElementWidth;
    ChartElementWidth: AnalyticsChartElementWidth;
    ColumnsSettings: ColumnSettingsSend;
}

// basic classes

export interface LinearChartSettingsBase extends ChartSettingsBase {
    StepType: LinearChartStepType;
    StepLength: number;
    DateRangeStart: Date;
    DateRangeEnd?: Date;
    DateRangeEndIsToday: boolean;
}

export interface PieChartBundleSettingsBase extends ChartSettingsBase {        
    DisplayType: PieChartBundleDisplayType;
}

export interface LinearChartCurveSettingsBase extends ChartElementSettingsBase {
    Color: string;
    LinearFunction: LinearChartFunction;
}

export interface PieChartSettingsBase extends ChartElementSettingsBase {       
    PartsOrderingType: AnalyticsOrderingType;
    DoRoundParts: boolean;
    MaxSeparatePartsCount: number;
}

export interface BarChartSettingsBase extends PieChartSettingsBase {      
    PartsDisplayType: BarChartDisplayType;
}

export interface ParameterForChart {
    Id: number;
    Name: string;
    Type: ParameterForChartType;
    RecordFields: ParameterForChart[];
}

export interface ParameterLink { 
    IdChain: number[];
}

// Result charts
export interface AnalyticsChartElementResultBase {
    Descriptor: AnalyticsChartElementResultDescriptor;
}

export enum AnalyticsChartElementResultDescriptor {
    Unknown = 0,
    LinearCurve = 1,
    SectorGroup = 2,
    Formula = 3,
    WorkloadLinearCurve = 4,
    WorkloadSectorGroup = 5,
}

// Linear
export interface AnalyticsChartResultBase {
    Name: string;
    ChartType: AnalyticsV2ChartType;
    LastForceRecalculatedAt?: Date;
}

export interface AnalyticsLinearChartResult extends AnalyticsChartResultBase {
    // В CurveResults могут лежать как стандартные кривые, так и результаты вычисления формул.
    CurveResults: AnalyticsChartElementResultBase[];
    XAxisValues: LinearChartPeriod[];
}

export interface LinearChartPeriod {
    StartDate: string;
    EndDate: string;
}

export interface AnalyticsLinearChartCurveResult extends AnalyticsChartElementResultBase {
    Name: string;
    Color: string;
    PointResults: AnalyticsLinearChartPointResult[];
    CalculateStatus: AnalyticsLinearChartCurveCalculateStatus
}

// Если CalculateStatus != Ok то кривая подсчитана с ошибкой и не должна быть отображена стандартным способом.

export interface AnalyticsLinearChartPointResult {
    Value: number; 
    EntityIds: number[];
}

// Pie

export interface AnalyticsChartSectorGroupResult extends AnalyticsChartElementResultBase {
    Name: string;
    SectorResults: AnalyticsChartSectorResult[];
    RepeatingEntityIds: number[];
    CalculateStatus: AnalyticsChartSectorCalculateStatus;
}

// Если CalculateStatus != Ok то группа секторов (а именно круговой график или столбиковый) подсчитана с ошибкой и не должна быть отображена стандартным способом.

export interface AnalyticsChartSectorResult
{
    Id: AnalyticsChartSectorId;
    Name: string;
    EntityIds: number[];
    Value: number;
}

export interface AnalyticsChartSectorId {
    EntityId: number;
    ParameterId: number;
    SplittingType: ChartUniqueSplitType;
}

export enum ChartUniqueSplitType {
    None = 0,
    JobStatus = 1,
    Worker = 2,
    Address = 3,
    Tag = 4,
    Stage = 5,
    Parameter = 6
}

export interface AnalyticsPieChartBundleResult extends AnalyticsChartResultBase {
    Name: string,
    DisplayType: PieChartBundleDisplayType,
    PieChartResults: AnalyticsChartElementResultBase[]
}

// Bar

export interface AnalyticsBarChartBundleResult extends AnalyticsChartResultBase {
    Name: string;
    BarChartResults: AnalyticsChartElementResultBase[];
}

// Formula

export interface ChartElementFormulaErrorResult {
    Type: ChartElementFormulaErrorResultType,
    Tokens: ChartElementFormulaTokenError[];
}

export enum ChartElementFormulaErrorResultType {
    Unknown = 0,
    CalculationError = 1,
    TokenErrors = 2
}

export interface ChartElementFormulaTokenError {
    Token: AnalyticsFormulaToken;
    ErrorType: ChartElementFormulaTokenErrorType;
}

export enum ChartElementFormulaTokenErrorType {
    Unknown = 0,
    FormulaNotFound = 1,
    FormulaNotCalculated = 2,
}

export interface AnalyticsChartElementWithFormulasResult extends AnalyticsChartElementResultBase {
    Name: string;
    FormulaResults: AnalyticsChartFormulaResult[];
}

export interface AnalyticsChartFormulaResult {
    Id: number;
    Name: string;
    Representation: AnalyticsChartElementFormula;

    Value?: number; 
    ErrorResult?: ChartElementFormulaErrorResult;
}

export interface TableElementWithFormulas {
    Identity: AnalyticsElementIdentity;
    Name: string;
    ChildElements: TableElementWithFormulas[];
    Formulas: CommonEntityRecord[];
}

export interface GlobalTableFormulasList {
    ChartOwnerRootElement: TableElementWithFormulas;
    OtherRootElements: TableElementWithFormulas[];
}
